/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2021 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: file-select-component.ts
 *
 * Created by kyang on 2021-01-22.
 */

//
// Allow user to select a file from their computer, or the server.
// If file is selected from the server, it is validated before the modal closes.
// However, if file is selected locally, value is changed while validation runs.
//     Component becomes invalid until the file passes validation.
//

// TODO: AB#87913 Merge with other implementations of file upload / selection
//     file-upload == embeddable version of file-upload-modal

function FileSelectController(
    this: JS5Object,
    _: JS5Object,
    $element: JS5Object,
    $q: JS5Object,
    FileSelectService: JS5Object,
    Rx: JS5Object,
) {

    enum FileSource {
        ASPERA = 'ASPERA',
        LOCAL = 'LOCAL',
        REMOTE = 'REMOTE'
    }

    enum FileValidationStatus {
        NONE = 'NONE',
        RUNNING = 'RUNNING',
        PASSED = 'PASSED',
        FAILED = 'FAILED'
    }

    let filesToTryAdding: JS5Object[] = [];
    let fileAddErrors: JS5Object[];

    //===================//
    // Template Response //
    //===================//

    const getStatus = (fileValue: JS5Object) => {
        return _.get(fileValue, 'status', FileValidationStatus.NONE);
    };

    const canSelectFileValue = (fileValue: JS5Object) => {
        return !this.isDisabled && getStatus(fileValue) !== FileValidationStatus.RUNNING;
    };

    const canSelectFile = () => {
        return canSelectFileValue(_.get(this, 'ngModelCtrl.$modelValue'));
    };

    this.isDragDropVisible = (dropWellSource: string) => {
        return _.get(this, 'dragDropSource', FileSource.LOCAL) === dropWellSource;
    };

    this.isFileSourceButtonVisible = (buttonSource: string) => {
        return _.chain(this)
            .get('buttonSources', [FileSource.LOCAL, FileSource.REMOTE])
            .includes(buttonSource)
            .value();
    };

    this.isFileSourceEnabled = (fileSource: string) => {
        return canSelectFile() && !_.includes(this.disabledSources, fileSource);
    };

    this.canShowValidationErrorModal = () => {
        return _.get(this, 'showValidationErrorModal', true);
    };

    this.isRemoteUploadEnabled = () => {
        return _.get(this, 'remoteUploadEnabled', true);
    }


    //=================//
    // File Validation //
    //=================//

    this.openWarningsPrompt = () => {
        if (!this.promptWarnings) {
            return $q.when(true);
        }
        return $q.when(this.promptWarnings());
    };

    this.openRemoteFileModal = () => {
        return this.openWarningsPrompt().then((canValidate: boolean) => {
            if (canValidate) {
                FileSelectService.promptFile(this, FileSource.REMOTE);
            }
        });
    };

    this.onAsperaFilesDropped = (asperaFileObjects: JS5Object[]) => {
        return this.openWarningsPrompt().then((canValidate: boolean) => {
            if (canValidate) {
                return FileSelectService.promptFile(this, FileSource.ASPERA, asperaFileObjects);
            }
        });
    };

    this.openLocalFileModal = () => {
        return this.openWarningsPrompt().then((canValidate: boolean) => {
            if (canValidate) {
                FileSelectService.promptFile(this, FileSource.LOCAL);
            }
        });
    };


    //=================//
    // Lifecycle Hooks //
    //=================//

    const onNgModelChanged = (newValue: JS5Object) => {
        this.fileReplacedObservable.onNext();
        this.loadingIndicator.show = getStatus(newValue) === FileValidationStatus.RUNNING;
        this.ngModelCtrl.$setValidity('canSelectFile', canSelectFileValue(newValue));
        this.ngModelCtrl.$setValidity('statusValue', getStatus(newValue) !== FileValidationStatus.FAILED);
        return newValue;
    };

    const onViewValueChanged = (newValue: JS5Object) => {
        this.loadingIndicator.show = getStatus(newValue) === FileValidationStatus.RUNNING;
        this.ngModelCtrl.$setValidity('canSelectFile', canSelectFileValue(newValue));
        this.ngModelCtrl.$setValidity('statusValue', getStatus(newValue) !== FileValidationStatus.FAILED);
        return newValue;
    };

    this.$onInit = () => {
        this.fileReplacedObservable = new Rx.Subject();
        this.$element = $element;
        this.loadingIndicator = {
            backdrop: false,
            show: false,
            size: 'lg',
            title: '',
            message: ''
        };
        FileSelectService.initialize(this);

        this.ngModelCtrl.$formatters.push(onNgModelChanged);
        this.ngModelCtrl.$parsers.push(onViewValueChanged);
    };

    this.$onDestroy = () => {
        this.fileReplacedObservable.onNext();
        this.fileReplacedObservable.onCompleted();
        FileSelectService.cleanup(this);
        delete this.$element;
    };
}

angular
    .module('prism-ui.components')
    .component('fileSelect', {
        templateUrl: 'components/fileSelect/file-select-template.html',
        controller: FileSelectController,
        require: {
            ngModelCtrl: '^ngModel',
        },
        bindings: {
            /*
             Configuration
             isMultiSelect:            (boolean; optional) If true, user can select
                                       multiple files.
             validateLocalMimeTypes:   (boolean; optional) If true, component will check
                                       local files' mimeTypes instead of file extensions.
                                       // TODO: AB#88420 Change all usages to always test
                                                fileType, not mimeType
             getFileExtensions:        (function; required) Function to determine
                                       which items to show in remote file upload modal.
                                       Should return an array of file extensions to allow.
             initializeUploaderConfig: (function; required) Function to call to
                                       configure FileUploader object. It should
                                       set the filter for the local file modal.
             promptWarnings:           (function; optional) Function to call before
                                       user can select a file; should return a promise.
                                       Promise should resolve to true if user decides
                                       to select files; false if they don't want to.
             getLocalFilePreValidationErrors: (function; required) Function to call
                                              when a FileUploader filter blocks local
                                              file selection.
                                              Should return errors to be displayed.
             remoteUploadEnabled:      (boolean; optional) If false, local uploads will
                                       be disabled. Defaults to true.
             validateRemoteFile: (function; required) Function to return a promise.
                                 Promise should be resolved with errors & warnings
                                 to be displayed.
                                 Called once for each remote file that's selected.
             validateAsperaFile: (function; optional) Function to return a promise.
                                 Promise should be resolved with errors & warnings
                                 to be displayed.
                                 Called once for each Aspera file that's selected.
                                 Required if dragDropSource == 'ASPERA'
             validateLocalFile:  (function; required) Function to return a promise.
                                 Promise should be resolved with errors & warnings
                                 to be displayed.
                                 Called once for each local file that's selected.
             ngModel: (any; required) Value == object with 4 properties:
                      - type:    (string) FileSource string that shows if the file
                                 is from local computer or the server.
                                 Value determines the format of the "file" object.
                      - files:   (JS5Object) The files that were selected.
                                 Always an array, even if allowMultipleFiles == false.
                      - results: (any | JS5Object[]) Validation results. If any
                                 validation promise is rejected, property == the
                                 rejected value.
                      - status:  (string) FileValidationStatus string that shows
                                 if this file has already been validated.
                      Replace the value to cancel local file validation.
                      Handled by ngModelCtrl.
             */
            isMultiSelect: '<?',
            validateLocalMimeTypes: '<?',
            getFileExtensions: '&',
            initializeUploaderConfig: '&',
            promptWarnings: '&?',
            getLocalFilePreValidationErrors: '&',
            remoteUploadEnabled: '<?',
            validateRemoteFile: '&',
            validateAsperaFile: '&?',
            validateLocalFile: '&',

            // Component attributes
            isDisabled: '<?ngDisabled',

            /*
             Display Attributes
             buttonSources:   (string[]; optional) Array of FileSource strings.
                              Only the buttons that support the specified FileSources
                              will be shown. Default is to show all FileSource buttons.
             dragDropSource:  (string; optional) Specifies the upload method to use
                              for this drop-zone. If value is null, drop well will not
                              be shown. Defaults to 'LOCAL'.
             disabledSources: (string[]; optional) Array of FileSource strings.
                              Disables the buttons & drop-zone for the listed
                              FileSources.
             dragDropId:      (string; optional) Id to set for the drag-drop zone.
                              Required if dragDropSource == 'ASPERA'
             dragDropClass:   (string; optional) Class to add to the drag-drop zone.
                              Can be used to resize the drop-zone.
             buttonTooltips:  (JS5Object; optional) Object of tooltips to display
                              for each file selection button.
                              - Key == FileSource
                              - value == tooltip to display for that source
             showValidationErrorModal: (boolean; optional) If true, will display a
                                       modal with validation failures / warnings.
                                       If false, modal will only be shown if local
                                       file could not be uploaded. Also, if false:
                                       user will not be able to confirm validations
                                       that only have warnings. Defaults to true.
             */
            buttonSources: '<?',
            dragDropSource: '<?',
            disabledSources: '<?',
            dragDropId: '<?',
            dragDropClass: '<?',
            buttonTooltips: '<?',
            showValidationErrorModal: '<?',

            /*
             Events
             ngChange: (function; optional) Is invoked every time
                       a file is selected, and when validation is finished.
                       Handled by ngModelCtrl.
             */
        }
    });
